import { MiddlemanService } from 'app/core/services/middleman.service';
import { SessionService } from 'app/core/services/session.service';
import { NotificationService } from 'app/core/services/notification.service';
import { SimpleInfoComponent } from 'app/shared/components/modal/simple-info.component';
import { SmartModalService, ModalSize } from 'app/core/services/smart-modal.service';
import { Subject } from 'rxjs/Subject';
import { Observable, Scheduler } from 'rxjs/';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { RxComponent } from 'app/core/components/common/rx.component';
import { Component, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AmberApi } from 'app/core/models/api-helpers';
import { ActivatedRoute, Router } from '@angular/router';
import {TemplatesService} from '../../services/templates.service';
import {EditorCommonComponent} from 'app/common/template/editor/editor.component';
import {PreviewCommonComponent} from 'app/common/template/preview/preview.component';
import {TemplateTransformer} from 'app/common/template/util/template-transformer';

declare var $: any;

@Component({
    selector: 'itpx-create-template',
    templateUrl: './create-template.component.html',
    styleUrls: ['./create-template.component.css'],
    animations: [
        trigger(
            'fadeInOut',
            [
                transition(
                    ':enter', [
                        style({opacity: 0}),
                        animate('250ms', style({'opacity': 1}))
                    ]
                ),
                transition(
                    ':leave', [
                        style({'opacity': 1}),
                        animate('10ms', style({'opacity': 0}))
                    ]
                )]
        )
    ]
})
export class CreateTemplateComponent extends RxComponent {

    private templates = [
        { key: 'solar_class_on_released_to_learner', value: 'Class released to learners' },
        { key: 'solar_class_on_released_to_instructor', value: 'Class released to instructor' }
    ];

    @ViewChildren(EditorCommonComponent) editors: QueryList<EditorCommonComponent>;
    @ViewChild('editorSection') editorSection;

    private idCount = 1;
    private form: FormGroup;
    private edit: boolean;
    private intent: any;

    // Inputs
    private previewClick = new Subject<any>();
    private templateSelect = new Subject<any>();
    private formSubmit = new Subject<any>();

    // Outputs
    private show: Observable<boolean>;
    private selectedTemplate: Observable<any>;
    private originalTemplate: Observable<any>;
    private chunks: Observable<any[]>;
    private chunksState: Observable<ChunksState>;
    private templateContentValid: Observable<boolean>;

    constructor(private fb: FormBuilder,
                private modalService: SmartModalService,
                private service: TemplatesService,
                private notificationService: NotificationService,
                private route: ActivatedRoute,
                private router: Router,
                private session: SessionService,
                private middlemanService: MiddlemanService
    ) {
        super();
    }

    ngOnInit() {

        this.form = this.fb.group({
            key: [null],
            text: ['-', Validators.required],
            html: ['-', Validators.required],
            from: [null, Validators.required],
            style: [null],
            type: ['handlebars', Validators.required],
            typeId: [null, Validators.required],
            subject: [null, Validators.required],
            chunks: new FormArray([])
        });

        this.intent = this.middlemanService.getData();
        this.middlemanService.clear();

        this.edit = this.route.snapshot.data['edit'];
        let template: Observable<any> = null;

        if (this.edit) {
            // We're in edit mode -> get template from path param
            let id = this.route.snapshot.params['id'];
            template = this.service.getTemplateById(id).neverError();
            this.form.get('key').disable();
        } else {
            // We're creating -> get template type from intent
            if (this.intent) {
                template = this.service.getTemplateByKey(this.intent.type).neverError();
            } else {
                // No intent (e.g. user refreshed page) -> wait for user input
                template = this.templateSelect
                    .flatMap(template => this.service.getTemplateByKey(template.key).neverError())
                    .take(1);
            }
        }


        this.selectedTemplate = template
            .extract(AmberApi.Data)
            .map(template => {
                template.displayValue = this.templates.find(t => t.key === template.typeId);
                // Assign ID to each chunk for tracking purposes
                for (let chunk of template.chunks) chunk.id = this.idCount++;
                return template;
            })
            .shareReplay();

        this.originalTemplate = this.selectedTemplate
            .flatMap(template => template.key === template.typeId ?
                Observable.of(template) :
                this.service.getTemplateByKey(template.typeId).neverError().extract(AmberApi.Data)
            );

        this.selectedTemplate
            .bindToLifecycle(this)
            .subscribe(template => {
                if (this.edit) {
                    this.form.patchValue({
                        key: template.key,
                        subject: template.subject,
                        typeId: template.typeId,
                        from: template.from,
                        style: template.style
                    });
                } else {
                    this.form.patchValue({
                        typeId: template.key,
                        from: template.from,
                        style: template.style
                    });
                }

                // Clear previous chunks (--before we allowed changing the dropdown value...)
                let chunksArray = this.form.controls.chunks as FormArray;
                for (let i = chunksArray.length - 1; i >= 0; i--) chunksArray.removeAt(i);

                // Add new ones
                for (let chunk of template.chunks) {
                    chunksArray.push(this.fb.group({
                        id: [chunk.id],
                        content: [chunk.content, Validators.required]
                    }));
                }
            });

        this.chunks = this.selectedTemplate
            .map(template => template.chunks)
            .shareReplay();

        this.chunksState = this.chunks
            .switchMap(chunks => this.chunksValueChange(this.form.controls.chunks as FormArray)
                .scan(({chunks, error}, chunkChange) => {
                        let id = chunkChange.id;
                        let content = chunkChange.value.content;

                        let parsedFields = TemplateTransformer.parseFields(content);
                        chunks[id].presentFields = parsedFields;

                        let containsIllegalField = Object.keys(parsedFields)
                            .some(key => !chunks[id].fields.find(f => f.key === key));

                        let containsAllRequiredFields = chunks[id].requiredFields
                            .every(requiredField => parsedFields[requiredField.key]);

                        chunks[id].valid = containsAllRequiredFields

                        return {
                            chunks,
                            error: containsIllegalField ? { chunkId: id, chunkPos: chunks[id].pos } : null
                        }
                    },
                    this.initChunkState(chunks))
            )
            .shareReplay();

        this.chunksState
            .map(state => state.error)
            .filter(err => err)
            .bindToLifecycle(this)
            .subscribe(chunkError => {
                let editor = this.editors.toArray()[chunkError.chunkPos];
                editor.undo();
            });

        this.templateContentValid = this.chunksState
            .map(state => state.chunks)
            .map(chunks => Object.keys(chunks).map(key => chunks[key]).every(c => c.valid))
            .share();

        this.previewClick
            .withLatestFrom(this.chunksState, (_, state) => this.getTemplateFromCombinedChunks(state.chunks))
            .switchMap(template => this.openPreviewModal(template).neverError())
            .bindToLifecycle(this)
            .subscribe();

        // !!! Nasty hack !!!
        // Delay show to next frame so angular destroys & re-initializes editor component
        this.show = this.selectedTemplate
            .flatMapTo(Observable.merge(
                Observable.of(false),
                Observable.of(true).subscribeOn(Scheduler.queue, 35)
            ))
            .startWith(false)
            .share();

        this.formSubmit
            .withLatestFrom(this.selectedTemplate, (form, template) => this.createDto(template, form))
            .flatMap(data => this.edit ?
                this.service.editTemplate(this.route.snapshot.params['id'], data).neverError() :
                this.service.createTemplate(data).neverError()
            )
            .extract(AmberApi.Data)
            .bindToLifecycle(this)
            .subscribe(template => {
                if (this.intent && this.intent.backRoute) {
                    this.middlemanService.navigateWithParams(
                        this.intent.backRoute.route,
                        this.intent.backRoute.queryParams,
                        { type: template.typeId, key: template.key, action: this.intent.action }
                    );
                } else {
                    if (this.edit) {
                        this.notificationService.success('Template edited.');
                        this.form.markAsPristine();
                    } else {
                        this.notificationService.success('Template created.');
                        this.router.navigate(['/organization/communications/templates', template.id, 'edit']);
                    }
                }
            });
    }

    private createDto(template: any, form: any): any {
        for (let i = 0; i < template.chunks.length; i++) {
            let placeholdersConfig = template.chunks[i].placeholders.reduce((config, p) => {
                config[p.key] = p.displayValue;
                return config;
            }, {});

            let content = TemplateTransformer.toRawTemplate(form.chunks[i].content, placeholdersConfig);
            form.chunks[i] = template.chunks[i];
            form.chunks[i].content = content;
        }

        return form;
    }

    private openPreviewModal(template: any): Observable<any> {
        let subject = this.form.get('subject').value || '';
        let title = `[Preview] ${subject}`;
        return this.modalService.open(PreviewCommonComponent,
            { template: template, css: this.form.get('style').value },
            title,
            ModalSize.Md,
            'right'
        );
    }

    private confirmChangeIfFormDirty(template: any): Observable<any> {
        if (this.form.controls.chunks.dirty) {
            return this.modalService.open(SimpleInfoComponent, {text: 'wrning', btn: 'btn-danger'}, 'Unsaved changes')
                .mapTo(template);
        }

        return Observable.of(template);
    }

    private chunksValueChange(formArray: FormArray): Observable<any> {
        // Track the values of all nested controls
        let controlsValueChangeObservables = formArray.controls
            .map(control => control.valueChanges
                .map(v => ({id: v.id, value: v}))
                .startWith({id: control.value.id, value: control.value})
            );

        // Deconstruct array to match Observable#merge signature
        return Observable.merge(...controlsValueChangeObservables);
    }

    private initChunkState(chunks: any[]): ChunksState {
        let chunksObj = chunks.reduce((state, value, pos) => {

            state[value.id] = {
                pos: pos,
                id: value.id,
                fields: value.placeholders,
                requiredFields: value.placeholders.filter(f => f.required),
            };

            return state;
        }, {});

        return new ChunksState(chunksObj);
    }

    private getTemplateFromCombinedChunks(state: any): String {
        let form = this.form.controls.chunks as FormGroup;
        let template = '';
        for (let chunk of Object.keys(form.controls)) {
            let content = form.controls[chunk].value.content;
            let fields = state[form.controls[chunk].value.id].fields;
            let fieldsToPlaceholder = {};
            for (let field of fields) {
                fieldsToPlaceholder[field.key] = field.previewValue;
            }

            template += TemplateTransformer.toPreviewTemplate(content, fieldsToPlaceholder);
        }

        return template;
    }

    add(chunkPos, item) {
        this.editors.toArray()[chunkPos].addPlaceholder({key: item.key, value: item.displayValue});
    }

    private onPreviewClick() {
        this.previewClick.next(true);
    }

    private onTemplateSelect(template) {
        this.templateSelect.next(template);
    }

    private onSubmit({value, valid}: {value: any, valid: boolean}) {
        this.formSubmit.next(this.form.getRawValue());
    }
}

class ChunksState {

    error: any;

    constructor(public chunks: { [key: string]: any }) {
    }
}



// WEBPACK FOOTER //
// ./src/app/organization/components/communication/templates/components/create/create-template.component.ts